<template>
  <div id="app" class="app">
    <router-view />
    
    <!-- Закрепленное уведомление о процессах (таймер + статус) -->
    <div v-if="activeGeneration" class="pinned-process-toast">
      <div class="pinned-process-header">
        <i class="fas fa-cog fa-spin"></i>
        <span>{{ activeGeneration.title }}</span>
      </div>
      <div class="pinned-process-body">
        <div class="timer-status">
          <span class="timer">⏱ {{ elapsedTime }}</span>
          <span class="status">{{ activeGeneration.statusText || 'В процесі...' }}</span>
        </div>
        <div class="progress-line">
          <div class="bar" :style="{ width: progressPercent + '%' }"></div>
        </div>
        <div class="details">
          <span v-if="hasTasks">{{ activeGeneration.completedTasks || 0 }} / {{ activeGeneration.totalTasks || 0 }}</span>
          <span v-if="activeGeneration.failedTasks && activeGeneration.failedTasks > 0" class="failed">
            Помилок: {{ activeGeneration.failedTasks }}
          </span>
          <span v-if="etaText" class="eta">ETА: {{ etaText }}</span>
        </div>
      </div>
      <button class="close-fixed" @click="dismissPinned">✕</button>
    </div>
    
    <!-- Глобальные уведомления -->
    <div class="notifications-container">
      <TransitionGroup name="notification">
        <div 
          v-for="(notif, index) in notifications" 
          :key="notif.id"
          class="global-notification" 
          :class="`notification-${notif.type}`"
          :style="getNotificationStyle(index)"
        >
          <i class="fas" :class="{
            'fa-check-circle': notif.type === 'success',
            'fa-exclamation-circle': notif.type === 'error',
            'fa-info-circle': notif.type === 'info'
          }"></i>
          <span>{{ notif.message }}</span>
          <button @click="removeNotification(notif.id)" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </TransitionGroup>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, provide, ref, computed, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { notificationService } from '@/services/notificationService'
import { processStatusService, type GenerationStatus } from '@/services/processStatusService'

// Композаблы
const { locale } = useI18n()

// Состояние уведомлений
const notifications = ref<Array<{ id: number; message: string; type: 'success' | 'error' | 'info' }>>([])
let notificationId = 0

// Вычисляем позиции для каждого уведомления
const getNotificationStyle = (index: number) => {
  const baseTop = 20
  const gap = 10
  const notificationHeight = 70 // примерная высота уведомления
  return {
    top: `${baseTop + index * (notificationHeight + gap)}px`,
    zIndex: 100000 - index // новые уведомления имеют больший z-index
  }
}

// Подписываемся на уведомления (вне onMounted для правильной области видимости)
notificationService.subscribe((message, type) => {
  const id = ++notificationId
  // Добавляем в начало массива, чтобы новые были сверху
  notifications.value.unshift({ id, message, type })
  
  // Автоматически удаляем через 5 секунд
  setTimeout(() => {
    const index = notifications.value.findIndex(n => n.id === id)
    if (index > -1) {
      notifications.value.splice(index, 1)
    }
  }, 5000)
})

// --- Закрепленное уведомление о генерации ---
const activeGeneration = ref<GenerationStatus | null>(null)
const timerInterval = ref<number | null>(null)
const nowTs = ref<number>(Date.now())

const hasTasks = computed(() => !!activeGeneration.value?.totalTasks)
const progressPercent = computed(() => Math.max(0, Math.min(100, activeGeneration.value?.progress ?? 0)))
const elapsedTime = computed(() => {
  if (!activeGeneration.value) return '—'
  const ms = nowTs.value - activeGeneration.value.startedAt
  const totalSeconds = Math.floor(ms / 1000)
  const h = Math.floor(totalSeconds / 3600)
  const m = Math.floor((totalSeconds % 3600) / 60)
  const s = totalSeconds % 60
  if (h > 0) return `${h}г ${m}хв ${s}с`
  if (m > 0) return `${m}хв ${s}с`
  return `${s}с`
})
const etaText = computed(() => {
  const eta = activeGeneration.value?.etaSeconds
  if (!eta || eta <= 0) return ''
  const m = Math.floor(eta / 60)
  const s = Math.floor(eta % 60)
  return m > 0 ? `${m}хв ${s}с` : `${s}с`
})

const unsubscribeProcess = processStatusService.subscribeGeneration((status) => {
  activeGeneration.value = status && status.isActive ? status : null
})

const tick = () => { nowTs.value = Date.now() }

onMounted(() => {
  timerInterval.value = window.setInterval(tick, 1000)
})

onUnmounted(() => {
  if (timerInterval.value) { clearInterval(timerInterval.value); timerInterval.value = null }
  if (typeof unsubscribeProcess === 'function') unsubscribeProcess()
})

function dismissPinned() {
  if (activeGeneration.value) {
    processStatusService.clearGeneration(activeGeneration.value.projectId)
  }
}

// Настройка украинского языка
onMounted(() => {
  locale.value = 'uk'
  document.documentElement.lang = 'uk'
  document.title = 'TDH Promodo - Професійна обробка семантичного ядра'
})

// Функция удаления уведомления
const removeNotification = (id: number) => {
  const index = notifications.value.findIndex(n => n.id === id)
  if (index > -1) {
    notifications.value.splice(index, 1)
  }
}

// Предоставляем глобальные сервисы
provide('app', {
  name: 'TDH Promodo',
  version: '1.0.0'
})
</script>

<style lang="scss">
.app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: $color-bg-dark;
  
  // Градиентные акценты как на изображении
  background-image: 
    radial-gradient(circle at 20% 50%, rgba(232, 65, 66, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(232, 65, 66, 0.05) 0%, transparent 50%);
    
  // Анимация появления
  animation: fade-in 0.3s ease-out;
}

// Прелоадер
.app-loading {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: $color-bg-dark;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  
  .loading-content {
    text-align: center;
    
    .loading-logo {
      font-family: $font-family-heading;
      font-size: $font-size-3xl;
      font-weight: $font-weight-bold;
      background: $gradient-primary;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      margin-bottom: $space-lg;
    }
    
    .loading-spinner {
      width: 40px;
      height: 40px;
      border: 3px solid $color-border;
      border-radius: 50%;
      border-top-color: $color-primary;
      animation: spin 1s linear infinite;
    }
    
    .loading-text {
      margin-top: $space-lg;
      color: $color-text-secondary;
      font-size: $font-size-sm;
    }
  }
}

// Глобальные переходы между страницами
.page-enter-active,
.page-leave-active {
  transition: all 0.3s ease-out;
}

.page-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.page-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

// Адаптивность для мобильных устройств
@media (max-width: $breakpoint-sm) {
  .app {
    background-image: 
      radial-gradient(circle at 50% 20%, rgba(232, 65, 66, 0.08) 0%, transparent 40%);
  }
}

// Контейнер для уведомлений
.notifications-container {
  position: fixed;
  top: 0;
  right: 0;
  z-index: 99999; // Увеличен z-index чтобы быть выше toast библиотеки
  pointer-events: none;
}

// Глобальные уведомления
.global-notification {
  position: absolute;
  right: 20px;
  min-width: 300px;
  max-width: 400px;
  min-height: 60px;
  padding: 16px 48px 16px 24px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 12px;
  pointer-events: all;
  transition: all 0.3s ease;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  
  i {
    font-size: 20px;
  }

  // Закрепленное уведомление о процессе
  .pinned-process-toast {
    position: fixed;
    right: 20px;
    bottom: 20px;
    width: 360px;
    background: #1f2937;
    color: #fff;
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 10px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.35);
    z-index: 100000;
    padding: 14px 42px 14px 14px;
  }

  .pinned-process-header {
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 700;
    margin-bottom: 8px;
  }

  .pinned-process-body {
    display: flex;
    flex-direction: column;
    gap: 8px;
    font-size: 14px;
  }

  .timer-status { display: flex; justify-content: space-between; opacity: 0.9; }
  .timer { font-variant-numeric: tabular-nums; }
  .status { color: #cbd5e1; }

  .progress-line {
    position: relative;
    height: 6px;
    background: rgba(255,255,255,0.12);
    border-radius: 999px;
    overflow: hidden;
  }
  .progress-line .bar {
    height: 100%;
    background: linear-gradient(90deg, #ef4444, #f59e0b, #22c55e);
    transition: width 0.4s ease;
  }
  .details { display: flex; gap: 10px; color: #cbd5e1; font-size: 12px; }
  .details .failed { color: #fca5a5; }
  .details .eta { margin-left: auto; }

  .close-fixed {
    position: absolute;
    right: 10px;
    top: 10px;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: rgba(255,255,255,0.06);
    color: #e5e7eb;
    border: none;
    cursor: pointer;
  }
  
  span {
    font-size: 16px;
    font-weight: 500;
    flex: 1;
  }
  
  .close-btn {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: inherit;
    cursor: pointer;
    padding: 4px;
    opacity: 0.7;
    transition: opacity 0.2s;
    
    &:hover {
      opacity: 1;
    }
  }
  
  &.notification-success {
    background-color: #28a745;
    color: white;
  }
  
  &.notification-error {
    background-color: #dc3545;
    color: white;
  }
  
  &.notification-info {
    background-color: #17a2b8;
    color: white;
  }
}

// Анимации для TransitionGroup
.notification-enter-active,
.notification-leave-active {
  transition: all 0.3s ease;
}

.notification-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.notification-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.notification-move {
  transition: transform 0.3s ease;
}
</style> 